import { useEffect, useRef, useState, type ChangeEvent, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import medicareCard from '@/assets/images/medicare-card-mockup.svg';
import { RecaptchaNotice } from '@/components/RecaptchaNotice';
import { RCta } from '../RCta';
import { RIcon } from '../RIcon';
import { WCta } from '../WCta';
import { WregPhase } from '../WregPhase';
import { WREG_WORKING_MS } from '../wregSteps';
import {
  EMPTY_INTAKE,
  MBI_MASK,
  dobIsOutOfRange,
  formatDob,
  formatMbi,
  formatPhone,
  intakeIsComplete,
  type WregIntakeForm,
  type WregIntakeTextField,
} from './wregIntakeFields';

type Props = {
  notice?: string | null;
  initial?: WregIntakeForm;
  onSubmit: (form: WregIntakeForm) => void | Promise<void>;
  onSubmitted?: () => void;
};

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      opacity=".35"
      d="M18,21H6c-1.657,0-3-1.343-3-3V7h18v11C21,19.657,19.657,21,18,21z"
    />
    <path d="M18,3H6C4.343,3,3,4.343,3,6v1h18V6C21,4.343,19.657,3,18,3z" />
    <circle cx="14" cy="18" r="1" />
    <circle cx="10" cy="18" r="1" />
    <circle cx="6" cy="18" r="1" />
    <circle cx="6" cy="14" r="1" />
    <circle cx="10" cy="14" r="1" />
    <circle cx="14" cy="14" r="1" />
    <circle cx="18" cy="14" r="1" />
    <circle cx="18" cy="10" r="1" />
    <circle cx="14" cy="10" r="1" />
    <circle cx="10" cy="10" r="1" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      opacity=".35"
      d="M16.48,3.086c-1.653,0.222-5.452,1.091-8.878,4.517s-4.295,7.224-4.517,8.878c-0.259,1.198,0.07,2.497,1.001,3.428c1.456,1.456,6.73-3.818,5.274-5.274l-0.929-0.929l5.274-5.274l0.929,0.929c1.456,1.456,6.73-3.818,5.274-5.274C18.977,3.155,17.678,2.826,16.48,3.086z"
    />
    <path d="M19.911,4.089l-5.273,5.273c1.457,1.453,3.815,1.453,5.271-0.003C21.363,7.905,21.364,5.546,19.911,4.089z" />
    <path d="M9.363,14.637L4.09,19.91c1.456,1.453,3.815,1.453,5.271-0.003C10.815,18.452,10.816,16.094,9.363,14.637z" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      d="M2,17V7c0-1.657,1.343-3,3-3h14c1.657,0,3,1.343,3,3v10c0,1.657-1.343,3-3,3H5C3.343,20,2,18.657,2,17z"
      opacity=".35"
    />
    <path d="M22,7c0-0.478-0.122-0.924-0.321-1.325l-8.187,4.912c-0.919,0.552-2.063,0.552-2.983,0L2.321,5.675C2.122,6.076,2,6.522,2,7v1.981l6.964,4.179c0.937,0.562,1.986,0.843,3.036,0.843c1.049,0,2.1-0.281,3.035-0.843L22,8.981V7z" />
  </svg>
);

const GENDERS = [
  ['Female', 'wreg.intake.gender.f'],
  ['Male', 'wreg.intake.gender.m'],
] as const;

const WregIntake = ({ notice, initial, onSubmit, onSubmitted }: Props) => {
  const { t } = useTranslation();
  const [f, setF] = useState<WregIntakeForm>(initial ?? EMPTY_INTAKE);
  const [mbiHelp, setMbiHelp] = useState(false);
  const [bfade, setBfade] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const fadeFor = (el: HTMLElement) => el.scrollTop + el.clientHeight < el.scrollHeight - 4;
  const onScroll = (e: UIEvent<HTMLDivElement>) => setBfade(fadeFor(e.currentTarget));

  useEffect(() => {
    setBfade(fadeFor(bodyRef.current as HTMLDivElement));
  }, []);

  const set = (k: WregIntakeTextField) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));

  const phoneDisplay = formatPhone(f.phone);
  const phone2Display = formatPhone(f.phone2);
  const phone2Mismatch =
    f.phone.length > 0 &&
    f.phone2.length > 0 &&
    !f.phone.startsWith(f.phone2) &&
    !f.phone2.startsWith(f.phone);
  const dobOutOfRange = dobIsOutOfRange(f.dob);
  const valid = intakeIsComplete(f);

  return (
    <div className="rg-screen">
      <div className="rg-body-wrap">
        <div className="rg-body" ref={bodyRef} onScroll={onScroll}>
          <div className="rg-inner">
            <div className="rg-head-block">
              <WregPhase n={1} />
              <h1 className="rg-h1">{t('wreg.intake.title')}</h1>
              <p className="rg-sub">{t('wreg.intake.sub')}</p>
            </div>
            {notice && (
              <div className="rg-disclosure" style={{ background: 'var(--hh-gold-4, #F6E3C9)' }}>
                <span className="ic" aria-hidden="true">i</span>
                <p>{notice}</p>
              </div>
            )}
            <div className="rg-field-row">
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-int-first">
                  {t('wreg.intake.first')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-int-first"
                    className="rg-input"
                    autoComplete="given-name"
                    value={f.first}
                    onChange={set('first')}
                  />
                </div>
              </div>
              <div className="rg-field">
                <label className="rg-label" htmlFor="wreg-int-last">
                  {t('wreg.intake.last')}
                </label>
                <div className="rg-input-wrap">
                  <input
                    id="wreg-int-last"
                    className="rg-input"
                    autoComplete="family-name"
                    value={f.last}
                    onChange={set('last')}
                  />
                </div>
              </div>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-dob">
                {t('wreg.intake.dob.label')}
              </label>
              <div className="rg-input-wrap wreg-mask-wrap">
                <span className="rg-input-lead">
                  <CalendarIcon />
                </span>
                <input
                  id="wreg-int-dob"
                  className="rg-input has-lead"
                  autoComplete="off"
                  inputMode="numeric"
                  value={f.dob}
                  onChange={(e) => setF((p) => ({ ...p, dob: formatDob(e.target.value) }))}
                  placeholder=""
                  aria-invalid={dobOutOfRange}
                  aria-describedby={dobOutOfRange ? 'wreg-int-dob-err' : undefined}
                />
                <span className="wreg-mask-ghost" aria-hidden="true">
                  <i>{f.dob}</i>
                  {t('wreg.intake.dob.ph').slice(f.dob.length)}
                </span>
              </div>
              {dobOutOfRange && (
                <span className="rg-error-hint" id="wreg-int-dob-err" role="alert">
                  {t('wreg.intake.dobnotice')}
                </span>
              )}
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-gender">
                {t('wreg.intake.gender.label')}
              </label>
              <div className="rg-input-wrap">
                <select
                  id="wreg-int-gender"
                  className={`rg-input rg-select${f.gender ? '' : ' placeholder'}`}
                  value={f.gender}
                  onChange={set('gender')}
                >
                  <option value="" disabled hidden>
                    {t('wreg.intake.gender.ph')}
                  </option>
                  {GENDERS.map(([g, key]) => (
                    <option key={g} value={g}>
                      {t(key)}
                    </option>
                  ))}
                </select>
                <span className="rg-select-chev" aria-hidden="true">
                  <RIcon.chevR />
                </span>
              </div>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-mbi">
                {t('wreg.intake.mbi.label')}
              </label>
              <div className="rg-input-wrap wreg-mbi-wrap">
                <input
                  id="wreg-int-mbi"
                  className="rg-input mbi"
                  autoComplete="off"
                  value={f.mbi}
                  onChange={(e) => setF((p) => ({ ...p, mbi: formatMbi(e.target.value) }))}
                  placeholder=""
                  style={{ width: '18.5ch', maxWidth: '100%' }}
                />
                <span className="wreg-mbi-ghost" aria-hidden="true">
                  <i>{f.mbi}</i>
                  {MBI_MASK.slice(f.mbi.length)}
                </span>
              </div>
              <span className="rg-hint">
                {t('wreg.intake.mbi.hint')}
                <button type="button" className="c-link" onClick={() => setMbiHelp(true)}>
                  {t('wreg.intake.mbi.how')}
                </button>
              </span>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-email">
                {t('wreg.intake.email.label')}
              </label>
              <div className="rg-input-wrap">
                <span className="rg-input-lead">
                  <MailIcon />
                </span>
                <input
                  id="wreg-int-email"
                  className="rg-input has-lead"
                  autoComplete="email"
                  type="email"
                  value={f.email}
                  onChange={set('email')}
                  placeholder={t('wreg.intake.email.ph')}
                />
              </div>
              <span className="rg-hint">{t('wreg.intake.email.hint')}</span>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-phone">
                {t('wreg.intake.phone.label')}
              </label>
              <div className="rg-input-wrap wreg-mask-wrap">
                <span className="rg-input-lead">
                  <PhoneIcon />
                </span>
                <input
                  id="wreg-int-phone"
                  className="rg-input has-lead"
                  autoComplete="tel-national"
                  type="tel"
                  inputMode="numeric"
                  value={phoneDisplay}
                  onChange={(e) =>
                    setF((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))
                  }
                  placeholder=""
                />
                <span className="wreg-mask-ghost" aria-hidden="true">
                  <i>{phoneDisplay}</i>
                  {t('wreg.intake.phone.ph').slice(phoneDisplay.length)}
                </span>
              </div>
              <span className="rg-hint">{t('wreg.intake.phone.hint')}</span>
            </div>
            <div className="rg-field">
              <label className="rg-label" htmlFor="wreg-int-phone2">
                {t('wreg.intake.phone2.label')}
              </label>
              <div className="rg-input-wrap wreg-mask-wrap">
                <span className="rg-input-lead">
                  <PhoneIcon />
                </span>
                <input
                  id="wreg-int-phone2"
                  className="rg-input has-lead"
                  autoComplete="off"
                  type="tel"
                  inputMode="numeric"
                  value={phone2Display}
                  onChange={(e) =>
                    setF((p) => ({ ...p, phone2: e.target.value.replace(/\D/g, '').slice(0, 10) }))
                  }
                  placeholder=""
                  aria-invalid={phone2Mismatch}
                  aria-describedby={phone2Mismatch ? 'wreg-int-phone2-err' : undefined}
                />
                <span className="wreg-mask-ghost" aria-hidden="true">
                  <i>{phone2Display}</i>
                  {t('wreg.intake.phone.ph').slice(phone2Display.length)}
                </span>
              </div>
              {phone2Mismatch && (
                <span className="rg-error-hint" id="wreg-int-phone2-err" role="alert">
                  {t('wreg.intake.phone2.err')}
                </span>
              )}
            </div>
            <label className={`c-checkopt wreg-consent${f.tcAccepted ? ' on' : ''}`}>
              <span className="wreg-consent-check">
                <input
                  type="checkbox"
                  className="wreg-consent-input"
                  checked={f.tcAccepted}
                  onChange={() => setF((p) => ({ ...p, tcAccepted: !p.tcAccepted }))}
                />
                <span className="c-checkopt-box" aria-hidden="true">
                  <RIcon.check />
                </span>
              </span>
              <span className="c-checkopt-body">
                <span className="c-checkopt-title">
                  {t('wreg.intake.legal.pre')}
                  <a
                    className="c-link"
                    href={t('general.links.tos')}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('wreg.intake.legal.terms')}
                  </a>
                  {t('wreg.intake.legal.post')}
                </span>
              </span>
            </label>
          </div>
        </div>
        <div className={`rg-list-fade${bfade ? ' show' : ''}`} aria-hidden="true" />
      </div>
      <div className="rg-foot">
        <div className="wreg-foot-row">
          <WCta
            disabled={!valid}
            working={t('wreg.intake.cta_loading')}
            minWorkingMs={WREG_WORKING_MS}
            onClick={() => onSubmit(f)}
            onDone={onSubmitted}
          >
            {t('wreg.intake.cta')}
          </WCta>
          {!valid && <span className="wreg-foot-tip">{t('wreg.foot.fillall')}</span>}
        </div>
        <div className="wreg-foot-legal">
          <RecaptchaNotice />
        </div>
      </div>

      {mbiHelp && (
        <div className="rg-modal-back show" onClick={() => setMbiHelp(false)}>
          <div className="rg-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="dsb dsb-secondary dsb-s wreg-modal-close"
              onClick={() => setMbiHelp(false)}
            >
              {t('wreg.mbi.close')}
            </button>
            <div className="rg-h1" style={{ paddingRight: 96 }}>
              {t('wreg.mbi.title')}
            </div>
            <p className="rg-sub">{t('wreg.mbi.sub')}</p>
            <img
              className="rg-mbi-img"
              src={medicareCard}
              width="400"
              height="260"
              alt={t('wreg.mbi.alt')}
            />
            <RCta onClick={() => setMbiHelp(false)}>{t('wreg.mbi.cta')}</RCta>
          </div>
        </div>
      )}
    </div>
  );
};

export { WregIntake };
